import React, { useState, useEffect, useRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './styles/klipiz-landing.css';

const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.socialcollector.app';
const APP_STORE_PLACEHOLDER = 'https://apps.apple.com/';

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const rootRef = useRef(null);
  const gradId = useId().replace(/:/g, '');

  const closeMenu = () => {
    setMenuOpen(false);
  };

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    const onScroll = () => {
      setHeaderScrolled(window.scrollY > 50);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const reveals = root.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const parent = entry.target.parentElement;
          const siblings = parent ? parent.querySelectorAll('.reveal') : [];
          let delay = 0;
          siblings.forEach((sib, idx) => {
            if (sib === entry.target) delay = idx * 80;
          });
          window.setTimeout(() => {
            entry.target.classList.add('is-visible');
          }, delay);
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    reveals.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const testimonials = [
    {
      text: t('landingTestimonial1Text'),
      name: t('landingTestimonial1Name'),
      role: t('landingTestimonial1Role'),
      rating: 5,
    },
    {
      text: t('landingTestimonial2Text'),
      name: t('landingTestimonial2Name'),
      role: t('landingTestimonial2Role'),
      rating: 5,
    },
    {
      text: t('landingTestimonial3Text'),
      name: t('landingTestimonial3Name'),
      role: t('landingTestimonial3Role'),
      rating: 5,
    },
  ];

  useEffect(() => {
    const n = 3;
    const interval = window.setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % n);
    }, 5000);
    return () => window.clearInterval(interval);
  }, []);

  const stats = [
    { number: '7', labelKey: 'statPlatforms' },
    { number: '∞', labelKey: 'statStorage' },
    { number: '100%', labelKey: 'statPrivate' },
    { number: '24/7', labelKey: 'statAccess' },
  ];

  const platforms = [
    { key: 'tiktok', name: 'TikTok', path: 'M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78 2.93 2.93 0 0 1 .88.13v-3.5a6.37 6.37 0 0 0-.88-.07 6.26 6.26 0 0 0 0 12.52 6.27 6.27 0 0 0 6.26-6.27V8.55a8.24 8.24 0 0 0 3.84.96V6.06a4.84 4.84 0 0 1-1.99.63Z' },
    { key: 'ig', name: 'Instagram', path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069ZM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0Zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881Z' },
    { key: 'yt', name: 'YouTube', path: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814ZM9.545 15.568V8.432L15.818 12l-6.273 3.568Z' },
    { key: 'fb', name: 'Facebook', path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073Z' },
    { key: 'x', nameKey: 'platformNameX', path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z' },
    { key: 'reddit', nameKey: 'platformNameReddit', path: 'M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0Zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701ZM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249Zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249Zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.094Z' },
    { key: 'spotify', nameKey: 'platformNameSpotify', path: 'M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0Zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02Zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2Zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3Z' },
    { key: 'wa', nameKey: 'platformNameWhatsApp', path: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z' },
  ];

  const featureCards = [
    {
      svg: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={`url(#${gradId})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      ),
      titleKey: 'landingFeat1Title',
      descKey: 'landingFeat1Desc',
    },
    {
      svg: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={`url(#${gradId})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      ),
      titleKey: 'landingFeat2Title',
      descKey: 'landingFeat2Desc',
    },
    {
      svg: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={`url(#${gradId})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      ),
      titleKey: 'landingFeat3Title',
      descKey: 'landingFeat3Desc',
    },
    {
      svg: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={`url(#${gradId})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      ),
      titleKey: 'landingFeat4Title',
      descKey: 'landingFeat4Desc',
    },
    {
      svg: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={`url(#${gradId})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 16 12 12 8 16" />
          <line x1="12" y1="12" x2="12" y2="21" />
          <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
        </svg>
      ),
      titleKey: 'landingFeat5Title',
      descKey: 'landingFeat5Desc',
    },
    {
      svg: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={`url(#${gradId})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      ),
      titleKey: 'landingFeat6Title',
      descKey: 'landingFeat6Desc',
    },
  ];

  const cur = testimonials[currentTestimonial];

  return (
    <div className="kl-landing" ref={rootRef}>
      <header className={`header${headerScrolled ? ' header--scrolled' : ''}`}>
        <div className="container">
          <Link to="/" className="logo" onClick={closeMenu}>
            <img src="/app_icon-k.jpg" alt="Klipiz" width="36" height="36" />
            <span>Klipiz</span>
          </Link>
          <div className="header__right">
            <nav className={`nav${menuOpen ? ' is-open' : ''}`} id="nav">
              <a href="#features" onClick={closeMenu}>
                {t('navFeatures')}
              </a>
              <a href="#how" onClick={closeMenu}>
                {t('navHow')}
              </a>
              <a href="#platforms" onClick={closeMenu}>
                {t('navPlatforms')}
              </a>
              <a href="#cta" className="btn btn--primary btn--sm" onClick={closeMenu}>
                {t('navTry')}
              </a>
            </nav>
            <div className="lang-switch">
              <button
                type="button"
                className={i18n.language.startsWith('fr') ? 'is-active' : ''}
                onClick={() => i18n.changeLanguage('fr')}
              >
                FR
              </button>
              <button
                type="button"
                className={i18n.language.startsWith('en') ? 'is-active' : ''}
                onClick={() => i18n.changeLanguage('en')}
              >
                EN
              </button>
            </div>
            <button
              type="button"
              className={`menu-toggle${menuOpen ? ' is-active' : ''}`}
              id="menu-toggle"
              aria-label="Menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      <section className="hero">
        <div className="hero__bg" aria-hidden="true">
          <div className="hero__orb hero__orb--1" />
          <div className="hero__orb hero__orb--2" />
          <div className="hero__orb hero__orb--3" />
          <div className="hero__grid-overlay" />
        </div>
        <div className="container hero__container">
          <div className="hero__content reveal">
            <div className="hero__badge">
              <span className="hero__badge-dot" />
              {t('landingHeroBadge')}
            </div>
            <h1 className="hero__title">
              {t('landingHeroTitle1')}
              <br />
              <span className="gradient-text">{t('landingHeroTitle2')}</span>
            </h1>
            <p className="hero__subtitle">{t('landingHeroSubtitle')}</p>
            <div className="hero__buttons">
              <a href="#cta" className="btn btn--primary">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {t('downloadNow')}
              </a>
              <a href="#how" className="btn btn--glass">
                {t('learnMore')}
              </a>
            </div>
            <div className="hero__social-proof">
              <div className="hero__platforms-row">
                <div className="hero__platform-icon" title="TikTok">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78 2.93 2.93 0 0 1 .88.13v-3.5a6.37 6.37 0 0 0-.88-.07 6.26 6.26 0 0 0 0 12.52 6.27 6.27 0 0 0 6.26-6.27V8.55a8.24 8.24 0 0 0 3.84.96V6.06a4.84 4.84 0 0 1-1.99.63Z" />
                  </svg>
                </div>
                <div className="hero__platform-icon" title="Instagram">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069ZM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0Zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881Z" />
                  </svg>
                </div>
                <div className="hero__platform-icon" title="YouTube">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814ZM9.545 15.568V8.432L15.818 12l-6.273 3.568Z" />
                  </svg>
                </div>
                <div className="hero__platform-icon" title="Facebook">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073Z" />
                  </svg>
                </div>
              </div>
              <span className="hero__social-text">{t('landingHeroSocialText')}</span>
            </div>
          </div>
          <div className="hero__phone reveal reveal--delay">
            <div className="hero__phone-frame">
              <div className="hero__phone-notch" />
              <img
                src="/app-screen1.png"
                alt=""
                className="hero__phone-screen"
                width="280"
                height="560"
              />
            </div>
            <div className="hero__phone-glow" aria-hidden="true" />
          </div>
        </div>
        <div className="hero__fade" aria-hidden="true" />
      </section>

      <section className="features" id="features">
        <div className="container">
          <div className="features__header reveal">
            <span className="section-label">{t('landingFeaturesLabel')}</span>
            <h2 className="section-title">
              {t('landingFeaturesTitle1')}
              <br />
              {t('landingFeaturesTitle2')}
            </h2>
            <p className="section-desc">{t('landingFeaturesDesc')}</p>
          </div>
          <div className="features__grid">
            {featureCards.map((card, i) => (
              <div key={i} className="feature-card reveal">
                <div className="feature-card__icon">{card.svg}</div>
                <h3 className="feature-card__title">{t(card.titleKey)}</h3>
                <p className="feature-card__desc">{t(card.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
        <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#4DB86A' }} />
              <stop offset="100%" style={{ stopColor: '#2ABFBF' }} />
            </linearGradient>
          </defs>
        </svg>
      </section>

      <div className="section-fade section-fade--light-to-white" aria-hidden="true" />

      <section className="how" id="how">
        <div className="container">
          <div className="how__header reveal">
            <span className="section-label">{t('landingHowLabel')}</span>
            <h2 className="section-title">
              {t('landingHowTitle1')}
              <br />
              {t('landingHowTitle2')}
            </h2>
            <p className="section-desc">{t('landingHowDesc')}</p>
          </div>
          <div className="how__steps">
            <div className="step reveal">
              <div className="step__visual">
                <div className="step__number">1</div>
                <div className="step__line" />
              </div>
              <div className="step__content">
                <h3 className="step__title">{t('landingStep1Title')}</h3>
                <p className="step__desc">{t('landingStep1Desc')}</p>
              </div>
            </div>
            <div className="step reveal">
              <div className="step__visual">
                <div className="step__number">2</div>
                <div className="step__line" />
              </div>
              <div className="step__content">
                <h3 className="step__title">{t('landingStep2Title')}</h3>
                <p className="step__desc">{t('landingStep2Desc')}</p>
              </div>
            </div>
            <div className="step reveal">
              <div className="step__visual">
                <div className="step__number">3</div>
              </div>
              <div className="step__content">
                <h3 className="step__title">{t('landingStep3Title')}</h3>
                <p className="step__desc">{t('landingStep3Desc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="section-fade section-fade--white-to-dark" aria-hidden="true" />

      <section className="platforms" id="platforms">
        <div className="platforms__bg" aria-hidden="true">
          <div className="platforms__orb platforms__orb--1" />
          <div className="platforms__orb platforms__orb--2" />
        </div>
        <div className="container">
          <div className="platforms__header reveal">
            <span className="section-label">{t('landingPlatformsLabel')}</span>
            <h2 className="section-title">
              {t('landingPlatformsTitle1')}
              <br />
              {t('landingPlatformsTitle2')}
            </h2>
            <p className="section-desc">{t('landingPlatformsDesc')}</p>
          </div>
          <div className="platforms__grid">
            {platforms.map((p) => (
              <div key={p.key} className="platform-card reveal">
                <div className="platform-card__icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                    <path d={p.path} />
                  </svg>
                </div>
                <div className="platform-card__name">
                  {p.nameKey ? t(p.nameKey) : p.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-fade section-fade--dark-to-white" aria-hidden="true" />

      <section className="landing-stats">
        <div className="container">
          <div className="landing-stats__grid">
            {stats.map((s, i) => (
              <div key={i} className="landing-stat reveal">
                <div className="landing-stat__num">{s.number}</div>
                <div className="landing-stat__label">{t(s.labelKey)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-testimonials" id="testimonials">
        <div className="container">
          <div className="landing-testimonials__header reveal">
            <span className="section-label">{t('lovedBy')}</span>
            <h2 className="section-title">
              {t('users')}
            </h2>
            <p className="section-desc" style={{ margin: '0 auto' }}>
              {t('testimonialsDescription')}
            </p>
          </div>
          <div className="testimonial-card reveal">
            <div className="testimonial-card__stars">
              {'★'.repeat(cur.rating)}
            </div>
            <p className="testimonial-card__text">&ldquo;{cur.text}&rdquo;</p>
            <div className="testimonial-card__name">{cur.name}</div>
            <div className="testimonial-card__role">{cur.role}</div>
          </div>
          <div className="testimonial-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                type="button"
                className={index === currentTestimonial ? 'is-active' : ''}
                aria-label={`Testimonial ${index + 1}`}
                onClick={() => setCurrentTestimonial(index)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="cta" id="cta">
        <div className="container">
          <div className="cta__box reveal">
            <div className="cta__glow" aria-hidden="true" />
            <div className="cta__content">
              <h2 className="cta__title">
                {t('landingCtaTitle1')}
                <br />
                {t('landingCtaTitle2')}
              </h2>
              <p className="cta__desc">{t('landingCtaDesc')}</p>
              <div className="cta__buttons">
                <a
                  href={GOOGLE_PLAY_URL}
                  className="btn btn--store"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.523 2.293a.665.665 0 0 0-.803.086L3.626 11.508a.665.665 0 0 0 0 .984L16.72 21.62a.665.665 0 0 0 1.052-.542V2.922a.665.665 0 0 0-.249-.629ZM3.299 11.148 1.209 12l2.09.852L14.47 4.16 3.299 11.148ZM14.47 19.84 3.299 12.852 1.209 12l2.09-.852L14.47 19.84ZM18.41 20.26V3.74l-3.055 7.97a.665.665 0 0 0 0 .58l3.054 7.97Z" />
                  </svg>
                  <div className="btn__store-text">
                    <span className="btn__store-label">{t('landingStoreLabel')}</span>
                    <span className="btn__store-name">{t('landingStoreGoogle')}</span>
                  </div>
                </a>
                <a
                  href={APP_STORE_PLACEHOLDER}
                  className="btn btn--store"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11Z" />
                  </svg>
                  <div className="btn__store-text">
                    <span className="btn__store-label">{t('landingStoreLabel')}</span>
                    <span className="btn__store-name">{t('landingStoreApple')}</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer__top">
            <div className="footer__brand">
              <img src="/app_icon-k.jpg" alt="" width="32" height="32" />
              <span>Klipiz</span>
            </div>
            <div className="footer__links">
              <Link to="/privacy" onClick={closeMenu}>
                {t('landingFooterPrivacy')}
              </Link>
              <span>{t('landingFooterTerms')}</span>
              <a href="mailto:support@klipiz.com">{t('footerContact')}</a>
              <Link to="/delete-account" onClick={closeMenu}>
                {t('deleteAccountLink')}
              </Link>
            </div>
          </div>
          <div className="footer__bottom">
            <p className="footer__copy">{t('landingCopyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
